<!-- SimplePDFPreview.vue -->
<template>
    <div class="simple-pdf-preview-container">
      <header class="pdf-viewer__header box-shadow">
      <PDFZoom
        :scale="scale"
        @change="updateScale"
        @fit="updateFit"
        class="header-item"
        />
      <slot name="header"></slot>
    </header>

      <div v-if="documentError" class="error-message">
        <v-icon color="red">mdi-alert-circle-outline</v-icon>
        <p>Error loading preview: {{ documentError }}</p>
      </div>

      <v-progress-linear
      indeterminate
      height="4"
      color="blue darken-2"
      v-if="lpage"
    ></v-progress-linear>
    <PDFData
      class="pdf-viewer__main"
      :url="url"
      @page-count="updatePageCount"
      @page-focus="updateCurrentPage"
      @document-rendered="onDocumentRendered"
      @document-errored="onDocumentErrored"
      >
      <template v-slot:preview="{pages}">
        <PDFPreview
          v-show="isPreviewEnabled"
          class="pdf-viewer__preview"
          v-bind="{pages, scale, currentPage, pageCount, isPreviewEnabled}"
          />
      </template>
      <template v-slot:document="{pages}">
        <PDFDocument
          class="pdf-viewer__document"
          :class="{ 'preview-enabled': isPreviewEnabled }"
          ref="pdfDoc"
          v-bind="{pages, scale, optimalScale, fit, currentPage, pageCount, isPreviewEnabled}"
          @scale-change="updateScale"
          />
      </template>
    </PDFData>
    </div>
  </template>

<script>
import PDFData from '@/components/pdf/components/PDFData'
import PDFPreview from '@/components/pdf/components/PDFPreview'
import PDFDocument from '@/components/pdf/components/PDFDocument'
import PDFZoom from '@/components/pdf/components/PDFZoom'

function floor (value, precision) {
  const multiplier = Math.pow(10, precision || 0)
  return Math.floor(value * multiplier) / multiplier
}

export default {
  name: 'SimplePDFPreview',
  components: {
    PDFData,
    PDFPreview,
    PDFDocument,
    PDFZoom
  },
  props: {
    url: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      pages: [],
      loading: false,
      documentError: null,
      scale: 3.50,
      optimalScale: 3.50,
      fit: 'width',
      currentPage: 1,
      pageCount: undefined,
      isPreviewEnabled: false,
      lpage: true,
      selectedSelectionInfo: null
    }
  },
  watch: {
    currentPageProp (newVal) {
      this.currentPage = newVal
    },
    url () {
      this.loading = true
      this.documentError = null
      this.pages = []
      this.pageCount = 0
      this.currentPage = this.currentPageProp
    }
  },
  methods: {
    onDocumentErrored (error) {
      console.error('PDF Preview Document Errored:', error)
      this.documentError = error || 'Failed to load PDF preview.'
      this.loading = false
    },
    updateScale ({ scale, isOptimal = false }) {
      this.lpage = false
      this.$emit('document-loaded', this.url)
      const roundedScale = floor(scale, 2)
      if (isOptimal) this.optimalScale = roundedScale
      this.scale = roundedScale
      this.$emit('onDocumentRendered', this.url)
    },

    updateFit (fit) {
      this.fit = fit
    },

    updatePageCount (pageCount) {
      this.pageCount = pageCount
    },

    updateCurrentPage (pageNumber) {
      this.currentPage = pageNumber
    }
  }
}
</script>

  <style scoped>
  .simple-pdf-preview-container {
    display: flex;
    flex-direction: column;
    height: 90vh;
    overflow-y: hidden;
    background-color: rgb(var(--secondary-color));
    border-right: 1px solid #e0e0e0;
    box-sizing: border-box;
  }

  .preview-title {
    font-size: 1.2em;
    font-weight: bold;
    margin-bottom: 15px;
    color: #333;
    text-align: center;
  }

  .error-message {
    color: #d32f2f;
    text-align: center;
    margin-top: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
  }

  .pdf-preview-thumbnails {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 15px;
  }

  .page-number-label {
    font-size: 0.9em;
    color: #555;
    margin-bottom: 5px;
  }

  .thumbnail-placeholder {
    width: 100px; /* Fixed width for the image */
    height: 120px; /* Fixed height for the image */
    background-color: #e0e0e0;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 0.8em;
    color: #666;
    border: 1px dashed #ccc;
    overflow: hidden; /* Ensure image fits */
    margin: 0 auto;
  }

  .thumbnail-image {
    width: 100%;
    height: 100%;
    object-fit: cover; /* Ensure the image covers the area */
  }

  .no-pages-message {
    text-align: center;
    color: #777;
    margin-top: 30px;
  }
  .pdf-preview-thumbnails ::v-deep .pdf-thumbnail.focused img,
  .pdf-preview-thumbnails ::v-deep .pdf-thumbnail.focused .placeholder {
    border: none !important;
    box-shadow: none !important;
    width: 90%;
  }
  .pdf-viewer__header {
    display: flex;
    justify-content: center;
  }
  .pdf-viewer__main{
    height: calc(90% - 42px);
  }
  .scrolling-document.pdf-document{
    height: 100%;
  }
  </style>
